import React, { useEffect, useState, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import Repo from "../../Utility/Repo";
import { TokenContext } from "../../Components/TokenContext";
import ModalComponent from "../../Components/ModalComponent";

const BatchUpdates = () => {
  const apiToken = useContext(TokenContext);
  const history = useHistory();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, toggleModal] = useState(false);

  const [selectedJob, setSelectedJob] = useState(null);

  const JOB_STATUS = {
    CREATED: 0,
    RUNNING: 1,
    COMPLETED: 2,
    COMPLETED_WITH_ERRORS: 3,
    ERROR: 4,
  };

  const isRunning = (bando) =>
    (bando.jobs || []).some(
      (j) => j.stato === JOB_STATUS.CREATED || j.stato === JOB_STATUS.RUNNING,
    );

  // ===============================
  // BADGE
  // ===============================
  const getJobBadge = (stato) => {
    switch (stato) {
      case JOB_STATUS.CREATED:
        return <span className="badge badge-secondary">Creato</span>;
      case JOB_STATUS.RUNNING:
        return <span className="badge badge-warning">In esecuzione</span>;
      case JOB_STATUS.COMPLETED:
        return <span className="badge badge-success">Completato</span>;
      case JOB_STATUS.COMPLETED_WITH_ERRORS:
        return (
          <span className="badge badge-warning">Completato con errori</span>
        );
      case JOB_STATUS.ERROR:
        return <span className="badge badge-danger">Errore</span>;
      default:
        return <span className="badge badge-light">-</span>;
    }
  };

  // ===============================
  // ULTIMO JOB PER TIPO
  // ===============================
  const getLastJobByTipo = (bando, tipo) => {
    if (!bando.jobs) return null;

    return bando.jobs
      .filter((j) => j.tipo === tipo)
      .sort(
        (a, b) => new Date(b.dataInsert || 0) - new Date(a.dataInsert || 0),
      )[0];
  };

  // ===============================
  // LOAD BANDI
  // ===============================

  useEffect(() => {
    const load = async () => {
      try {
        const response = await Repo.getAggiornamentiBandi(apiToken);
        setData(response.data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [apiToken]);

  useEffect(() => {
    const hasRunning = data.some((bando) =>
      (bando.jobs || []).some(
        (j) => j.stato === JOB_STATUS.CREATED || j.stato === JOB_STATUS.RUNNING,
      ),
    );

    if (!hasRunning) return;

    console.log("Polling attivo");

    const interval = setInterval(async () => {
      try {
        const response = await Repo.getAggiornamentiBandi(apiToken);
        setData(response.data);
      } catch (err) {
        console.log(err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [data, apiToken]);

  const openStartModal = (id, type) => {
    setSelectedJob({
      id,
      type,
    });

    toggleModal(true);
  };

  // ===============================
  // START JOB
  // ===============================
  const startJob = async () => {
    try {
      const tipoMap = {
        ISEE: 1,
        ANPR: 2,
        ALL: 3,
      };

      const body = {
        announcementId: selectedJob.id,
        tipo: tipoMap[selectedJob.type],
      };

      await Repo.startAggiornamento(apiToken, body);

      const response = await Repo.getAggiornamentiBandi(apiToken);

      setData(response.data);

      toggleModal(false);
    } catch (err) {
      console.log(err);
    }
  };

  // ===============================
  // RENDER CELLA
  // ===============================
  const renderUpdateCell = (bando, tipo, date) => {
    const job = getLastJobByTipo(bando, tipo);

    return (
      <div className="d-flex flex-column align-items-center">
        {/* DATA CLICKABILE */}
        {job ? (
          <button
            className="btn btn-link p-0 text-decoration-underline"
            onClick={() =>
              history.push(`/gestione_aggiornamenti/${job.jobId}/logs`)
            }
          >
            {date || "-"}
          </button>
        ) : (
          <span>{date || "-"}</span>
        )}

        {/* BADGE */}
        {job && <div className="mt-1">{getJobBadge(job.stato)}</div>}

        {/* CONTATORI */}
        {job && (
          <div className="small text-muted mt-1">
            {job.completati} / {job.totale}
            {job.errori > 0 && ` - Errori: ${job.errori}`}
          </div>
        )}
      </div>
    );
  };

  // ===============================
  // RENDER
  // ===============================
  if (loading) return <p>Caricamento...</p>;

  return (
    <div className="container">
      <h4 className="mb-4">Gestione aggiornamenti massivi</h4>

      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Bando</th>
              <th className="text-center">ANPR</th>
              <th className="text-center">ISEE</th>
              <th className="text-center" style={{ width: "300px" }}>
                Aggiorna
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((bando) => (
              <tr key={bando.id}>
                <td>{bando.nome} ({bando.users})</td>

                <td className="text-center">
                  {renderUpdateCell(bando, 2, bando.anprLastUpdate)}
                </td>

                <td className="text-center">
                  {renderUpdateCell(bando, 1, bando.iseeLastUpdate)}
                </td>

                <td className="text-center">
                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    <button
                      className="btn btn-outline-primary btn-sm mr-2"
                      disabled={isRunning(bando)}
                      onClick={() => openStartModal(bando.id, "ANPR")}
                    >
                      ANPR
                    </button>

                    <button
                      className="btn btn-outline-success btn-sm mr-2"
                      disabled={isRunning(bando)}
                      onClick={() => openStartModal(bando.id, "ISEE")}
                    >
                      ISEE
                    </button>

                    <button
                      className="btn btn-outline-dark btn-sm mr-2"
                      disabled={isRunning(bando)}
                      onClick={() => openStartModal(bando.id, "ALL")}
                    >
                      Tutto
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ModalComponent
        toggleModal={toggleModal}
        isOpen={isOpen}
        header="Conferma aggiornamento"
        body={`Confermi di voler avviare l'aggiornamento ${
          selectedJob?.type || ""
        }?`}
        buttonConfirm="Conferma"
        buttonDeny="Annulla"
        function={() => startJob()}
      />
    </div>
  );
};

export default BatchUpdates;
